// Baked-in default schemas for the three handoff feedback surfaces
// (RET-1069 V2.1). These are the fallback when the PostHog feature-flag
// `lead_agent_handoff_options` payload is missing or malformed.
//
// V2.1: every display string is inline per locale (`labels: { es, en, br }`),
// not an i18n key reference. Producto edits the JSON payload in PostHog UI
// to add NEW options, remove existing ones, reorder, or re-label across
// any locale — fully self-serve, no Vite rebuild, no PR.
//
// `category` slugs are stable analytics values. Renaming a slug fragments
// existing dashboards (different `reason_category` buckets pre/post). The
// safe pattern is: add a new slug + label, don't rename an existing one.

import type {
  tHandoffReasonEntryPoint,
  tHandoffReasonSchema,
} from './handoffReasonOptions.types';

// Surface 1 — Per-chat "Tomar control" pill in MessageBar SLB banner.
// Flat, optional. Operator can Skip or close freely.
const TOMAR_CONTROL_SCHEMA: tHandoffReasonSchema = {
  q1Title: {
    es: '¿Qué te hizo tomar el control?',
    en: 'What made you take over?',
    br: 'O que te fez assumir o controle?',
  },
  q1Subtitle: {
    es: 'Es opcional, nos ayuda a mejorar',
    en: 'Optional — helps us improve',
    br: 'Opcional — nos ajuda a melhorar',
  },
  mandatory: false,
  options: [
    {
      category: 'hot_lead_self_close',
      labels: {
        es: 'Lead caliente, lo cierro yo',
        en: "Hot lead — I'll close it myself",
        br: 'Lead quente, vou fechar eu mesmo',
      },
    },
    {
      category: 'bot_wrong',
      labels: {
        es: 'El bot respondió mal',
        en: 'The bot answered wrong',
        br: 'O bot respondeu mal',
      },
    },
    {
      category: 'sensitive_complaint',
      labels: {
        es: 'Tema sensible / queja',
        en: 'Sensitive topic / complaint',
        br: 'Tema sensível / reclamação',
      },
    },
    {
      category: 'other',
      labels: {
        es: 'Otra razón',
        en: 'Something else',
        br: 'Outro motivo',
      },
      showTextarea: true,
    },
  ],
};

// Surface 2 — Per-chat "Apagar el bot" toggle inside the AIResponsePreview
// popup (the sparkle-icon button's portal). 2-level nested, mandatory.
const APAGAR_BOT_CHAT_SCHEMA: tHandoffReasonSchema = {
  q1Title: {
    es: 'Apagaste el Lead Agent en esta conversación. ¿Qué pasó?',
    en: 'You turned off the Lead Agent in this conversation. What happened?',
    br: 'Você desligou o Lead Agent nesta conversa. O que aconteceu?',
  },
  subQuestion: {
    es: '¿Qué fue exactamente?',
    en: 'What exactly happened?',
    br: 'O que foi exatamente?',
  },
  q2Label: {
    es: '¿Quieres contarnos algo más? (Opcional)',
    en: 'Want to tell us more? (Optional)',
    br: 'Quer nos contar algo mais? (Opcional)',
  },
  mandatory: true,
  options: [
    {
      category: 'bot_wrong_response',
      labels: {
        es: 'El Lead Agent respondió mal',
        en: 'The Lead Agent answered wrong',
        br: 'O Lead Agent respondeu mal',
      },
      subOptions: [
        {
          category: 'incorrect_info',
          labels: {
            es: 'Dijo algo incorrecto',
            en: 'Said something incorrect',
            br: 'Disse algo incorreto',
          },
        },
        {
          category: 'didnt_understand',
          labels: {
            es: 'No entendió la pregunta',
            en: "Didn't understand the question",
            br: 'Não entendeu a pergunta',
          },
        },
        {
          category: 'wrong_tone',
          labels: {
            es: 'El tono fue inadecuado',
            en: 'The tone was inappropriate',
            br: 'O tom foi inadequado',
          },
        },
        {
          category: 'repeats_messages',
          labels: {
            es: 'Repite las mismas respuestas',
            en: 'Repeats the same answers',
            br: 'Repete as mesmas respostas',
          },
        },
      ],
    },
    {
      category: 'lacks_capability',
      labels: {
        es: 'Le falta capacidad para este caso',
        en: 'Lacks capability for this case',
        br: 'Falta capacidade para este caso',
      },
      subOptions: [
        {
          category: 'missing_business_info',
          labels: {
            es: 'Falta información de mi negocio',
            en: 'Missing business information',
            br: 'Falta informação do meu negócio',
          },
        },
        {
          category: 'cant_close_sale',
          labels: {
            es: 'No puede cerrar la venta',
            en: "Can't close the sale",
            br: 'Não consegue fechar a venda',
          },
        },
        {
          category: 'missing_integration',
          labels: {
            es: 'Falta integración con mis herramientas',
            en: 'Missing integration with my tools',
            br: 'Falta integração com minhas ferramentas',
          },
        },
        {
          category: 'no_personalization',
          labels: {
            es: 'No personaliza con datos del lead',
            en: "Doesn't personalize with lead data",
            br: 'Não personaliza com dados do lead',
          },
        },
      ],
    },
    {
      category: 'prefer_handling_myself',
      labels: {
        es: 'Prefiero atender yo',
        en: 'I prefer to handle this myself',
        br: 'Prefiro atender eu mesmo',
      },
      subOptions: [
        {
          category: 'hot_lead',
          labels: {
            es: 'Es un lead caliente',
            en: "It's a hot lead",
            br: 'É um lead quente',
          },
        },
        {
          category: 'vip_client',
          labels: { es: 'Cliente VIP', en: 'VIP client', br: 'Cliente VIP' },
        },
        {
          category: 'sensitive_topic',
          labels: {
            es: 'Tema sensible o queja',
            en: 'Sensitive topic or complaint',
            br: 'Tema sensível ou reclamação',
          },
        },
        {
          category: 'sale_closed',
          labels: {
            es: 'Ya cerré la venta',
            en: 'I already closed the sale',
            br: 'Já fechei a venda',
          },
        },
      ],
    },
    {
      category: 'technical_error',
      labels: {
        es: 'Hubo un error técnico',
        en: 'There was a technical error',
        br: 'Houve um erro técnico',
      },
      subOptions: [
        {
          category: 'no_response',
          labels: {
            es: 'No respondió',
            en: "Didn't respond",
            br: 'Não respondeu',
          },
        },
        {
          category: 'too_slow',
          labels: {
            es: 'Tardó demasiado',
            en: 'Took too long',
            br: 'Demorou muito',
          },
        },
        {
          category: 'weird_message',
          labels: {
            es: 'Mandó un mensaje raro',
            en: 'Sent a weird message',
            br: 'Mandou uma mensagem estranha',
          },
        },
        {
          category: 'crashed',
          labels: { es: 'Se cayó', en: 'Crashed', br: 'Caiu' },
        },
      ],
    },
    {
      category: 'other',
      labels: {
        es: 'Otra razón',
        en: 'Something else',
        br: 'Outro motivo',
      },
      showTextarea: true,
    },
  ],
};

// Surface 3 — Workspace-wide "Desactivar" from ConfigHeader 3-dot menu
// in smart-config. 2-level nested, mandatory. Distinct option pool because
// the intent is "stop the agent for my whole workspace" (product fit
// signal), not "this conversation needs a human" (per-chat tactical signal).
const DESACTIVAR_WORKSPACE_SCHEMA: tHandoffReasonSchema = {
  q1Title: {
    es: 'Apagaste el Lead Agent. ¿Cuál fue la razón principal?',
    en: 'You turned off the Lead Agent. What was the main reason?',
    br: 'Você desligou o Lead Agent. Qual foi a razão principal?',
  },
  q1Subtitle: {
    es: 'Tu respuesta nos ayuda a mejorar el producto.',
    en: 'Your answer helps us improve the product.',
    br: 'Sua resposta nos ajuda a melhorar o produto.',
  },
  subQuestion: {
    es: '¿Puedes ser más específico?',
    en: 'Can you be more specific?',
    br: 'Pode ser mais específico?',
  },
  q2Label: {
    es: 'Si quieres, cuéntanos más (Opcional)',
    en: "If you'd like, tell us more (Optional)",
    br: 'Se quiser, conte-nos mais (Opcional)',
  },
  mandatory: true,
  options: [
    {
      category: 'not_results',
      labels: {
        es: 'No me da los resultados que esperaba',
        en: 'Not getting the results I expected',
        br: 'Não me dá os resultados que eu esperava',
      },
      subOptions: [
        {
          category: 'doesnt_qualify_leads',
          labels: {
            es: 'No califica bien los leads',
            en: "Doesn't qualify leads well",
            br: 'Não qualifica bem os leads',
          },
        },
        {
          category: 'no_conversions',
          labels: {
            es: 'No genera conversiones',
            en: "Doesn't generate conversions",
            br: 'Não gera conversões',
          },
        },
        {
          category: 'doesnt_save_time',
          labels: {
            es: 'No me ahorra tiempo',
            en: "Doesn't save me time",
            br: 'Não me economiza tempo',
          },
        },
        {
          category: 'cant_measure_impact',
          labels: {
            es: 'No sé cómo medir su impacto',
            en: "I can't measure its impact",
            br: 'Não sei como medir seu impacto',
          },
        },
      ],
    },
    {
      category: 'response_quality',
      labels: {
        es: 'La calidad de las respuestas no es buena',
        en: "Response quality isn't good",
        br: 'A qualidade das respostas não é boa',
      },
      subOptions: [
        {
          category: 'incorrect_answers',
          labels: {
            es: 'Responde cosas incorrectas',
            en: 'Answers things incorrectly',
            br: 'Responde coisas incorretas',
          },
        },
        {
          category: 'doesnt_understand_clients',
          labels: {
            es: 'No entiende a mis clientes',
            en: "Doesn't understand my clients",
            br: 'Não entende meus clientes',
          },
        },
        {
          category: 'tone_off_brand',
          labels: {
            es: 'El tono no representa a mi marca',
            en: "The tone doesn't represent my brand",
            br: 'O tom não representa minha marca',
          },
        },
        {
          category: 'repeats_messages',
          labels: {
            es: 'Repite las mismas respuestas',
            en: 'Repeats the same answers',
            br: 'Repete as mesmas respostas',
          },
        },
      ],
    },
    {
      category: 'missing_capabilities',
      labels: {
        es: 'Le faltan capacidades que necesito',
        en: 'Lacks capabilities I need',
        br: 'Faltam capacidades que eu preciso',
      },
      subOptions: [
        {
          category: 'no_close_or_charge',
          labels: {
            es: 'No cierra ventas ni cobra',
            en: "Doesn't close sales or charge",
            br: 'Não fecha vendas nem cobra',
          },
        },
        {
          category: 'no_scheduling',
          labels: {
            es: 'No agenda citas',
            en: "Doesn't schedule appointments",
            br: 'Não agenda reuniões',
          },
        },
        {
          category: 'missing_integration',
          labels: {
            es: 'Falta integración con mis herramientas',
            en: 'Missing integration with my tools',
            br: 'Falta integração com minhas ferramentas',
          },
        },
        {
          category: 'doesnt_know_business',
          labels: {
            es: 'No conoce bien mi negocio',
            en: "Doesn't know my business well",
            br: 'Não conhece bem meu negócio',
          },
        },
      ],
    },
    {
      category: 'technical_errors_frequent',
      labels: {
        es: 'Tiene errores técnicos frecuentes',
        en: 'Frequent technical errors',
        br: 'Tem erros técnicos frequentes',
      },
      subOptions: [
        {
          category: 'no_response',
          labels: {
            es: 'No respondió',
            en: "Didn't respond",
            br: 'Não respondeu',
          },
        },
        {
          category: 'too_slow',
          labels: {
            es: 'Tardó demasiado',
            en: 'Took too long',
            br: 'Demorou muito',
          },
        },
        {
          category: 'weird_message',
          labels: {
            es: 'Mandó un mensaje raro',
            en: 'Sent a weird message',
            br: 'Mandou uma mensagem estranha',
          },
        },
        {
          category: 'crashes_or_stuck',
          labels: {
            es: 'Se cae o se traba',
            en: 'Crashes or freezes',
            br: 'Cai ou trava',
          },
        },
      ],
    },
    {
      category: 'migrated_elsewhere',
      labels: {
        es: 'Migré a otra herramienta',
        en: 'I migrated to another tool',
        br: 'Migrei para outra ferramenta',
      },
      subOptions: [
        {
          category: 'found_better',
          labels: {
            es: 'Encontré una mejor',
            en: 'Found a better one',
            br: 'Encontrei uma melhor',
          },
        },
        {
          category: 'stack_changed',
          labels: {
            es: 'Mi empresa cambió de stack',
            en: 'My company changed stack',
            br: 'Minha empresa mudou de stack',
          },
        },
        {
          category: 'resumed_previous',
          labels: {
            es: 'Estaba usando otra y la retomé',
            en: 'I was using another and went back',
            br: 'Estava usando outra e voltei',
          },
        },
      ],
    },
    {
      category: 'other',
      labels: {
        es: 'Otra razón',
        en: 'Something else',
        br: 'Outro motivo',
      },
      showTextarea: true,
    },
  ],
};

export const DEFAULT_HANDOFF_SCHEMAS: Record<
  tHandoffReasonEntryPoint,
  tHandoffReasonSchema
> = {
  tomar_control: TOMAR_CONTROL_SCHEMA,
  apagar_bot_chat: APAGAR_BOT_CHAT_SCHEMA,
  desactivar_workspace: DESACTIVAR_WORKSPACE_SCHEMA,
};
